// bundleReduce.js
//
// Pure, dependency-free bundle reduction math, shared by:
//   - the server gate (api/_utils/bundleAvailability.js) — supply comes from the
//     check_availability_enhanced RPC, and
//   - the edit display (src/components/manage/AddProductsSection.jsx) — supply
//     comes from /api/availability MINUS what the in-progress cart consumes.
//
// Keeping ONE implementation of the demand-aggregation + Hall's-condition
// reduction avoids the kind of client/server divergence (e.g. MAX vs SUM for
// size-less components) that caused availability displays to disagree with the
// booking gate.
//
// "Supply" is injected via getSupply(productTypeId) so each caller decides how a
// component's availability is sourced and (for the display) how cart
// consumption is subtracted.

export const SIZELESS = Symbol('sizeless'); // demand/supply key for components with no fixed size

/**
 * @typedef {Object} ComponentSupply
 * @property {number} totalAvailable               supply summed across all sizes
 * @property {Map<string, number>} availableBySize per fixed-size supply (size string -> count)
 * @property {string|null} inventoryType           'individual' | 'pool' | 'unlimited' | null
 */

/**
 * Reduce a bundle to how many whole copies are fulfillable.
 *
 * Feasibility per component (Hall's condition; size-less demand is fully flexible):
 *   - every fixed size s : supply(s) >= demand_fixed(s)
 *   - total supply        >= total demand
 * The bundle count is the floor of the tightest constraint across all components.
 * A fixed size on a pool/unlimited component is ignored (pools have no per-size
 * inventory) — same as the authoritative create-booking check.
 *
 * @param {Array<{product_type_id:string,size?:string|null,quantity?:number}>} bundleComponents
 * @param {(productTypeId:string) => ComponentSupply} getSupply
 * @returns {{ bundleCount:number, components:Array<{product_type_id:string,available:number,neededPerBundle:number,supports:number,limiting:boolean}> }}
 */
export function reduceBundle(bundleComponents, getSupply) {
  if (!Array.isArray(bundleComponents) || bundleComponents.length === 0) {
    return { bundleCount: 0, components: [] };
  }

  // Aggregate demand by product type, summing quantities and bucketing by fixed
  // size vs size-less. This closes the shared-component gap (two entries on the
  // same product type contend for one pool instead of being checked twice).
  const demandByProduct = new Map(); // id -> { total, bySize: Map<size|SIZELESS, qty> }
  for (const c of bundleComponents) {
    const qty = Math.max(Number(c.quantity) || 1, 1); // GREATEST(quantity, 1)
    const sizeKey = c.size != null && c.size !== '' ? c.size : SIZELESS;
    let entry = demandByProduct.get(c.product_type_id);
    if (!entry) {
      entry = { total: 0, bySize: new Map() };
      demandByProduct.set(c.product_type_id, entry);
    }
    entry.total += qty;
    entry.bySize.set(sizeKey, (entry.bySize.get(sizeKey) || 0) + qty);
  }

  const components = [];
  let bundleCount = Infinity;

  for (const [productTypeId, demand] of demandByProduct) {
    const { totalAvailable, availableBySize, inventoryType } = getSupply(productTypeId);
    const total = totalAvailable || 0;

    let supports = Math.floor(total / demand.total);

    if (inventoryType === 'individual') {
      for (const [sizeKey, qty] of demand.bySize) {
        if (sizeKey === SIZELESS) continue; // covered by the total constraint
        const supplyForSize = (availableBySize && availableBySize.get(sizeKey)) || 0;
        supports = Math.min(supports, Math.floor(supplyForSize / qty));
      }
    }

    components.push({
      product_type_id: productTypeId,
      available: total,
      neededPerBundle: demand.total,
      supports,
      limiting: false, // set below
    });
    bundleCount = Math.min(bundleCount, supports);
  }

  bundleCount = Number.isFinite(bundleCount) ? Math.max(0, bundleCount) : 0;
  for (const c of components) c.limiting = c.supports === bundleCount;

  return { bundleCount, components };
}
